import { useCallback, useEffect, useState } from "react";
import { usePaginatedSearch } from "../../hooks/use-paginated-search";
import { Spinner, Table } from "react-bootstrap";
import moment from "moment";
import { api } from "../../helpers/apiCore";
import { endpoints } from "../../helpers/Endpoints";
import { SCHOOL_PAYMENTS } from "../../lib/config";
import { formatCellValue } from "../../lib/format-cell";
import { PaymentData } from "../../types";
import type { ReactNode } from "react";
import FormInput from "../../components/FormInput";
import ExportPopover from "../../components/ExportPopover";
import PaginationComponent from "../../components/PaginationComponent";
import searchIcon from "../../assets/icons/search.svg";
import SchoolPaymentDetailsModal from "./SchoolPaymentDetailsModal";


const SchoolPaymentsTable = () => {
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedPayment, setSelectedPayment] = useState<PaymentData | null>(null);

  const {
    searchTerm,
    debouncedSearchTerm,
    paginationData,
    setPaginationData,
    handleSearchChange,
    handlePaginate
  } = usePaginatedSearch(10);

  const fetchPayments = useCallback(async () => {
    setIsLoading(true);
    try {
      api.setAuthorization(api.getLoggedInUser());
      const queryParams = new URLSearchParams({
        page: paginationData.currentPage.toString(),
        per_page: paginationData.perPage.toString(),
        sortBy: 'created_at',
        sortOrder: 'desc',
      });
      if (debouncedSearchTerm.trim()) queryParams.set('searchQuery', debouncedSearchTerm.trim());
      if (startDate) {
        queryParams.set('startDate', moment(startDate).format('YYYY-MM-DD'));
      }
      if (endDate) {
        queryParams.set('endDate', moment(endDate).format('YYYY-MM-DD'));
      }

      const response = await api.get(endpoints.getSchoolPayments, queryParams);
      const { data, pageInfo } = response.data;

      setPayments(data || []);
      setPaginationData(prev => ({
        ...prev,
        currentPage: pageInfo.currentPage,
        totalPages: pageInfo.totalPages,
        total: pageInfo.total,
        perPage: pageInfo.perPage
      }));
    } catch (error) {
      console.error("Error fetching payments:", error);
      setPayments([]);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearchTerm, paginationData.currentPage, paginationData.perPage, setPaginationData, startDate, endDate]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const getSerialNumber = (index: number) =>
    (paginationData.currentPage - 1) * paginationData.perPage + index + 1;

  return (
    <>
      <div className="table-topbar">
        <div className="search_bar">
          <img src={searchIcon} alt="search" />
          <FormInput
            name="search"
            placeholder="Search payments..."
            className="search-bar-input"
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
          />
        </div>

        <div className="range-input-section mb-3 mt-2">
          <FormInput
            label="From:"
            type="date"
            name="startDate"
            value={startDate}
            onChange={e => {
              setStartDate((e.target as HTMLInputElement).value);
              setPaginationData(prev => ({ ...prev, currentPage: 1 }));
            }}
          />
          <FormInput
            label="To:"
            type="date"
            name="endDate"
            value={endDate}
            onChange={e => {
              setEndDate((e.target as HTMLInputElement).value);
              setPaginationData(prev => ({ ...prev, currentPage: 1 }));
            }}
          />
        </div>

        <ExportPopover
          exportConfig="schoolPayments"
          searchQuery={debouncedSearchTerm}
          dateRange={{
            startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : undefined,
            endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : undefined,
          }}
          // filters={{ status: selectedStatus }}
          // dateRange={{ startDate, endDate }}
          disabled={payments?.length === 0}
          availableFormats={['csv', 'xlsx', 'pdf']}
          onExportStart={() => console.log('Export started')}
          onExportComplete={() => console.log('Export complete')}
          onExportError={console.error}
        />
      </div>

      <div className="table-body">
        <Table>
          <thead>
            <tr>
              {/* <th className="custom-thead-bg">S/N</th> */}
              {SCHOOL_PAYMENTS.map(col => (
                <th key={col.key} className="custom-thead-bg">{col.label}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={SCHOOL_PAYMENTS.length + 1} className="text-center">
                  <Spinner animation="border" size="sm" />
                </td>
              </tr>
            ) : payments?.length === 0 ? (
              <tr>
                <td colSpan={SCHOOL_PAYMENTS.length + 1} className="text-center">
                  No payments found.
                </td>
              </tr>
            ) : (
              payments?.map((payment, index) => (
                <tr
                  key={payment.id}
                  className="clickable-row"
                  onClick={() => setSelectedPayment(payment)}
                >
                  {/* <td className="custom-tbody">{getSerialNumber(index)}</td> */}
                  {SCHOOL_PAYMENTS.map(col => (
                    <td key={col.key} className="custom-tbody">
                      {formatCellValue(col, payment) as ReactNode}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {payments?.length > 0 && (
        <PaginationComponent
          currentPage={paginationData.currentPage}
          totalPages={paginationData.totalPages}
          hasNextPage={paginationData.currentPage < paginationData.totalPages}
          hasPreviousPage={paginationData.currentPage > 1}
          nextPage={paginationData.currentPage + 1}
          previousPage={paginationData.currentPage - 1}
          onPaginate={handlePaginate}
        />
      )}

      {selectedPayment && (
        <SchoolPaymentDetailsModal
          payment={selectedPayment}
          onHide={() => setSelectedPayment(null)}
        />
      )}
    </>
  );
};

export default SchoolPaymentsTable;
